<i18n
  src="@/locales/views/producer/projections/estimated_harvest/edit_estimated_harvest.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/estimated_harvest.yml" lang="yaml"></i18n>
<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('show_harvest_process_estimated_harvests')"
    :params="{
      producer_id: producer_id,
      harvest_process_id: harvest_process_id
    }"
  />

  <bread-crumbs :labels="[module_name, harvest_process?.name]"></bread-crumbs>

  <alert-danger :errors_data="std_err" />

  <div v-if="load_general_data" class="mt-8">
    <div class="panel">
      <div class="panel-header">
        <h3 class="panel-header-title">
          {{ $t("labels.title") }}
        </h3>
      </div>

      <div class="panel-body">
        <div class="space-y-6">
          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.estimated_harvest.harvest_date") }}
              </label>
              <div class="mt-1">
                <date-form v-model="estimated_harvest.harvest_date" />
              </div>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.estimated_harvest.crop_field") }}
              </label>
              <div v-if="crop_fields.length == 1" class="mt-1">
                {{ crop_fields[0].name }}
              </div>
              <div v-else class="mt-1">
                <select-form
                  :options="crop_fields"
                  v-model="estimated_harvest.crop_field_id"
                  label="name"
                  :clearable="false"
                  :is_invalid="isInvalidSelect('crop_field')"
                />
              </div>
            </div>
            <div class="sm:col-span-3" v-if="crop_field_sectors.length > 0">
              <label class="label-form">
                {{ $t("model.estimated_harvest.crop_field_sector") }}
              </label>
              <div v-if="crop_field_sectors.length == 1" class="mt-1">
                {{ crop_field_sectors[0].name }}
              </div>
              <div v-else class="mt-1">
                <select-form
                  :options="crop_field_sectors"
                  v-model="estimated_harvest.crop_field_sector_id"
                  label="name"
                  :clearable="false"
                  :is_invalid="isInvalidSelect('crop_field_sector')"
                />
              </div>
            </div>
            <div class="sm:col-span-3">
              <label class="label-form">
                {{ $t("model.estimated_harvest.num_packages") }}
              </label>
              <div class="mt-1 flex w-full">
                <input
                  type="text"
                  v-model.trim="estimated_harvest.num_packages"
                  :class="['input-text', isInvalid('num_packages')]"
                />
                <select-form
                  class="w-1/3"
                  :options="harvest_process_crop_packages"
                  v-model="estimated_harvest.harvest_process_crop_package_id"
                  label="name"
                  :clearable="false"
                  :disabled="harvest_process_crop_packages.length == 1"
                  :is_invalid="isInvalidSelect('harvest_process_crop_package')"
                />
              </div>
            </div>
          </div>
          <p>
            Porcentajes
            <span v-if="sum_percents != 100" class="text-amber-500">
              <i class="fa-solid fa-triangle-exclamation"></i> Deben sumar 100%
            </span>
          </p>

          <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <template
              v-for="(
                crop_size_group, index
              ) in harvest_process_crop_size_groups"
              :key="crop_size_group.id"
            >
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ crop_size_group.name }} (%)
                </label>
                <div class="mt-1 flex relative">
                  <input
                    type="text"
                    v-model.trim="estimated_harvest_groups[index].percent"
                    class="input-text pr-6"
                  />
                  <div
                    class="flex items-center absolute inset-y-0 right-0 mr-3 text-sm leading-5 font-bold text-gray-600"
                  >
                    %
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="panel-footer">
        <div class="panel-footer-actions">
          <white-button
            @click.prevent="goToShowHarvestProcess()"
            :label="$t('labels.cancel_button')"
          />
          <span class="space-actions"></span>
          <success-button
            v-if="!proc_save_button"
            @click.prevent="update()"
            :label="$t('labels.save_button')"
          />
          <success-button-processing v-else :label="$t('labels.save_button')" />
        </div>
      </div>
    </div>
  </div>

  <loading-message v-else />
</template>

<script>
import Requester from "@/views/producer/requesters/projections/estimated_harvest/manage_estimated_harvest.js";

export default {
  name: "ProdProjectionsEstimatedHarvestNewEstimatedHarvest",
  data() {
    return {
      load_general_data: false,
      proc_save_button: false,
      harvest_process: {},
      estimated_harvest: {},
      estimated_harvest_groups: [],
      harvest_process_crop_packages: [],
      harvest_process_crop_size_groups: [],
      crop_fields: [],
      crop_field_sectors: []
    };
  },
  props: {
    producer_id: {
      required: true
    },
    harvest_process_id: {
      required: true
    },
    estimated_harvest_id: {
      required: true
    }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          producer_id: this.producer_id
        }
      }).then(() => this.loadData())
    );
  },
  computed: {
    estimated_harvest_group_params: function () {
      return this.estimated_harvest_groups.filter(estimated_harvest_group => {
        return (
          estimated_harvest_group.percent !== undefined &&
          Number(estimated_harvest_group.percent) > 0
        );
      });
    },
    sum_percents: function () {
      return this.estimated_harvest_groups.reduce(
        (sum, el) => Number(el.percent) + sum,
        0
      );
    }
  },
  methods: {
    loadData: async function () {
      await this.fetchHarvestProcess();
      await this.fetchHarvestProcessCropSizeGroups();
      await this.fetchCropFields();
      await this.fetchEstimatedHarvest();
      await this.fetchHarvestProcessCropPackages();
      this.load_general_data = true;
    },
    fetchHarvestProcess: async function () {
      let response = await Requester.fetchHarvestProcess(
        this.producer_id,
        this.harvest_process_id
      );
      this.harvest_process = response.data;
    },
    fetchEstimatedHarvest: async function () {
      let response = await Requester.fetchEstimatedHarvest(
        this.producer_id,
        this.estimated_harvest_id
      );
      this.estimated_harvest = response.data;
      this.harvest_process_crop_size_groups.forEach(crop_size_group => {
        this.estimated_harvest_groups.push({
          harvest_process_crop_size_group_id: crop_size_group.id,
          percent:
            this.estimated_harvest.estimated_harvest_groups.find(
              estimated_harvest_group =>
                estimated_harvest_group.harvest_process_crop_size_group_id ==
                crop_size_group.id
            )?.percent || ""
        });
      });
      this.getFieldSectors()
    },
    fetchCropFields: async function () {
      let response = await Requester.fetchCropFields(
        this.producer_id,
        this.harvest_process_id
      );
      this.crop_fields = response.data;
      if (this.crop_fields.length == 1) {
        this.estimated_harvest.crop_field_id = this.crop_fields[0].id;
      }
    },
    getFieldSectors: function () {
      this.crop_field_sectors =
        this.crop_fields.find(
          crop_field => crop_field.id == this.estimated_harvest.crop_field_id
        )?.crop_field_sectors || [];
      if (this.crop_field_sectors.length == 1) {
        this.estimated_harvest.crop_field_sector_id =
          this.crop_field_sectors[0].id;
      }
    },
    fetchHarvestProcessCropPackages: async function () {
      let response = await Requester.fetchHarvestProcessCropPackages(
        this.producer_id,
        this.harvest_process_id
      );
      this.harvest_process_crop_packages = response.data;
      if (this.harvest_process_crop_packages.length == 1) {
        this.estimated_harvest.harvest_process_crop_package_id =
          this.harvest_process_crop_packages[0].id;
      }
    },
    fetchHarvestProcessCropSizeGroups: async function () {
      let response = await Requester.fetchHarvestProcessCropSizeGroups(
        this.producer_id,
        this.harvest_process_id
      );
      this.harvest_process_crop_size_groups = response.data;
    },
    update: async function () {
      this.proc_save_button = true;
      try {
        let response = await Requester.update(
          this.producer_id,
          this.estimated_harvest_id,
          {
            ...this.estimated_harvest,
            estimated_harvest_groups: this.estimated_harvest_group_params
          }
        );
        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.goToShowHarvestProcess();
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields
          });
        }
      }
      this.proc_save_button = false;
    },
    goToShowHarvestProcess: function () {
      this.$router.push({
        name: this.routeName("show_harvest_process"),
        params: {
          harvest_process_id: this.harvest_process_id,
          producer_id: this.producer_id
        }
      });
    }
  },
  watch: {
    "estimated_harvest.crop_field_id": function () {
      this.getFieldSectors();
    }
  }
};
</script>
